import { addDays, isSameDay } from 'date-fns';
import { combineLatest, from, merge, of } from 'rxjs';
import { catchError, filter, map, mergeMap, take, withLatestFrom } from 'rxjs/operators';
import { Action } from 'typesafe-actions';
import { createSaveTableSettingsEpic } from '../../../common/datagrid';
import { ResourceType, TableType } from '../../../common/PricePrgClient';
import { notifyPriceCommentUpdated, notifyPricePlansUpdated } from '../../../common/signalr/signalrActions';
import { CommentType } from '../../../common/signalr/signalrTypes';
import { RootEpic } from '../../../store';
import { createEpicFromAsyncAction, getAllPricelistChildren, notNull, toDateString } from '../../../utils';
import { fetchPricelistPeriodsForPricelists } from '../../customer/redux/customerSlices';
import { fetchUserSettings } from '../../settings/redux/settingsSlices';
import { fetchStocksForDay } from '../../stock/redux/stockSlices';
import { enqueueSnackbar, pricePrgError, showConfirmDialog } from '../../ui/redux/uiSlices';
import { fetchCurrentUserPermissions } from '../../user/redux/userSlices';
import {
    calculateProductPrices,
    cancelPriceUpdate,
    fetchPrices,
    fetchPricesPartial,
    pricelistDateSelected,
    priceTableActions,
    priceTablePricelistsSelected,
    savePriceComment,
    updatePrices,
    warehouseDateSelected,
    warehouseSelected
} from './priceSlices';

const dispatchCalculateProductPrices$: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(calculateProductPrices.match),
        withLatestFrom(
            state$.pipe(map(state => state.price.selectedDate)),
            state$.pipe(map(state => state.price.ui.applyCalculations))
        ),
        mergeMap(([action, pricelistDate, applyCalculations]) =>
            of(
                updatePrices.request({
                    price: action.payload.currentPrice,
                    productId: action.payload.productId,
                    pricelistId: action.payload.pricelistId,
                    date: pricelistDate,
                    awareOfCreatingNewPricelistPeriods: false,
                    isAwareOfChangingActualPricelist: false,
                    applyCalculations: applyCalculations
                })
            )
        )
    );

const pricelistsSelected$: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(priceTablePricelistsSelected.match),
        withLatestFrom(state$.pipe(map(state => state.price.selectedDate))),
        map(([action, pricelistDate]) =>
            fetchPrices.request({
                pricelistIds: action.payload,
                validAt: pricelistDate
            })
        )
    );

const pricelistDateSelected$: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(pricelistDateSelected.match),
        withLatestFrom(state$.pipe(map(state => state.price.selectedPricelistIds))),
        map(([action, pricelistIds]) =>
            fetchPrices.request({
                pricelistIds: pricelistIds,
                validAt: action.payload
            })
        )
    );

const fetchWarehouseBalance$: RootEpic = (action$, state$) =>
    merge(
        action$.pipe(
            filter(warehouseSelected.match),
            withLatestFrom(state$.pipe(map(state => state.price.selectedWarehouse.date))),
            map(([action, selectedDate]) =>
                fetchStocksForDay.request({
                    date: selectedDate,
                    warehouseId: action.payload
                })
            )
        ),
        action$.pipe(
            filter(warehouseDateSelected.match),
            withLatestFrom(state$.pipe(map(state => state.price.selectedWarehouse.warehouse))),
            filter(([, warehouse]) => warehouse != null),
            map(([action, warehouse]) =>
                fetchStocksForDay.request({
                    date: toDateString(action.payload),
                    warehouseId: warehouse!
                })
            )
        )
    );

const fetchPrices$ = createEpicFromAsyncAction(
    fetchPrices,
    (payload, { priceClient }) => priceClient.getPrices(payload.pricelistIds, payload.validAt),
    resp => fetchPricelistPeriodsForPricelists.request(resp.pricelistIds)
);

const fetchPricesPartial$ = createEpicFromAsyncAction(fetchPricesPartial, (payload, { priceClient }) =>
    priceClient.getPricesPartial(payload)
);

const updatePrice$: RootEpic = (action$, state$, { priceClient }) =>
    action$.pipe(
        filter(updatePrices.request.match),
        withLatestFrom(
            state$.pipe(map(state => state.price.selectedPricelistIds)),
            state$.pipe(map(state => state.price.selectedDate)),
            state$.pipe(map(state => state.pricelist.pricelists))
        ),
        mergeMap(([action, selectedPricelistIds, selectedDate, pricelists]) => {
            if (
                !action.payload.isAwareOfChangingActualPricelist &&
                isSameDay(new Date(action.payload.date), addDays(new Date(), 1))
            ) {
                return of(
                    showConfirmDialog({
                        dialogTitle: 'Changing actual pricelist',
                        dialogMessage:
                            'You are about to change a price on the actual pricelist. Are you sure you want do to that?',
                        primaryActionName: 'Save',
                        okAction: updatePrices.request({
                            ...action.payload,
                            isAwareOfChangingActualPricelist: true
                        }),
                        cancelAction: cancelPriceUpdate({
                            pricelistId: action.payload.pricelistId,
                            productId: action.payload.productId
                        })
                    })
                );
            }

            return from(priceClient.updatePrice(action.payload)).pipe(
                mergeMap(response => {
                    const { payload } = action;
                    if (response?.message != null) {
                        return of(
                            showConfirmDialog({
                                dialogTitle: 'New pricelist period will be created',
                                dialogMessage: `Pricelist period is missing for ${payload.date}. By clicking create it will automatically be created.`,
                                primaryActionName: 'Create pricelist period',
                                okAction: updatePrices.request({
                                    ...payload,
                                    awareOfCreatingNewPricelistPeriods: true,
                                    refetchPrices:
                                        response.pricesWillBeCopied !==
                                        response.previousPricelistPeriodWillBeCut
                                }),
                                cancelAction: cancelPriceUpdate({
                                    pricelistId: payload.pricelistId,
                                    productId: payload.productId
                                })
                            })
                        );
                    }

                    const actions: Action[] = [
                        updatePrices.success({
                            pricelistPeriodCopied: action.payload.awareOfCreatingNewPricelistPeriods,
                            pricelistIdsCopied: [
                                action.payload.pricelistId,
                                ...getAllPricelistChildren(
                                    action.payload.pricelistId,
                                    Object.values(pricelists)
                                )
                            ],
                            productId: action.payload.productId
                        }),
                        notifyPricePlansUpdated({
                            pricelistId: payload.pricelistId,
                            productId: payload.productId
                        })
                    ];

                    if (action.payload.refetchPrices === true) {
                        actions.push(
                            fetchPrices.request({
                                pricelistIds: selectedPricelistIds,
                                validAt: selectedDate
                            })
                        );
                    }

                    return from(actions);
                }),
                catchError(() =>
                    of(
                        enqueueSnackbar({
                            message: 'Failed to update price',
                            severity: 'error',
                            autoHideDuration: 15000
                        }),
                        cancelPriceUpdate({
                            pricelistId: action.payload.pricelistId,
                            productId: action.payload.productId
                        })
                    )
                )
            );
        })
    );

const savePriceTable$ = createSaveTableSettingsEpic(priceTableActions, state => state.price.ui.priceTable, [
    priceTablePricelistsSelected
]);

const priceTableSettingsLoadedFetchPrices$: RootEpic = (action$, state$) =>
    // Use combine latest in order to not proceed with fetching prices until
    // both userSettings and currentUserPermissions has been fetched.
    combineLatest([
        action$.pipe(filter(fetchUserSettings.success.match)),
        action$.pipe(filter(fetchCurrentUserPermissions.success.match), take(1))
    ]).pipe(
        filter(
            ([, cup]) => cup.payload.isAdmin || cup.payload.accessiblePages.includes(ResourceType.PricePage)
        ),
        withLatestFrom(
            state$.pipe(map(state => state.price.selectedDate)),
            state$.pipe(map(state => state.price.selectedPricelistIds))
        ),
        map(([[userSettings], selectedDate, selectedPricelistIds]) => {
            const priceTable = userSettings.payload?.tableSettings.find(ts => ts.table === TableType.Price);

            return priceTable != null
                ? fetchPrices.request({
                      pricelistIds: selectedPricelistIds,
                      validAt: selectedDate
                  })
                : null;
        }),
        filter(notNull)
    );

const savePriceComment$: RootEpic = (action$, state$, { priceClient }) =>
    action$.pipe(
        filter(savePriceComment.request.match),
        withLatestFrom(
            state$.pipe(map(state => state.pricelist.pricelists)),
            state$.pipe(map(state => state.price.prices))
        ),
        mergeMap(([action, pricelists, prices]) =>
            from(priceClient.updatePriceComment(action.payload.priceId, action.payload.comment ?? '')).pipe(
                mergeMap(() => {
                    const priceIds = [action.payload.priceId];
                    const pricelist = pricelists[action.payload.pricelistId];

                    if (pricelist?.applyCommentsToChildren) {
                        priceIds.push(
                            ...getAllPricelistChildren(action.payload.pricelistId, Object.values(pricelists))
                                .map(plId => prices[`${action.payload.productId}-${plId}`]?.priceId)
                                .filter(notNull)
                        );
                    }

                    return of(
                        savePriceComment.success({
                            comment: action.payload.comment,
                            priceIds: priceIds
                        }),
                        notifyPriceCommentUpdated({
                            comment: action.payload.comment,
                            priceIds: [action.payload.priceId],
                            commentType: CommentType.InPrice,
                            pricelistId: action.payload.pricelistId,
                            productId: action.payload.productId
                        })
                    );
                }),
                catchError(err => of(pricePrgError(err)))
            )
        )
    );

export default [
    dispatchCalculateProductPrices$,
    fetchPrices$,
    fetchPricesPartial$,
    pricelistsSelected$,
    pricelistDateSelected$,
    updatePrice$,
    fetchWarehouseBalance$,
    savePriceTable$,
    priceTableSettingsLoadedFetchPrices$,
    savePriceComment$
];
